import React, { useMemo } from 'react';
import BarrasHorizontais from '../shared/BarrasHorizontais';
import ColunasMensais from '../shared/ColunasMensais';
import { rotuloDoMes } from '../../../utils/calculations/receita';
import {
  etapaAtual, concluido, filaPorEtapa, funilDeProducao,
  resumoOperacional, operacaoPorCliente, servicosPorMes,
} from '../../../utils/calculations/operacao';
import { Destaque, Indicador, Cartao, Tabela, Vazio, BotaoPDF, num, pct } from './Pecas';
import { gerarRelatorioPDF, descreverFiltros } from '../../../utils/relatorioPDF';

/**
 * Relatório de serviços: onde está o trabalho, e o que ele produziu.
 *
 * Ele liderava por FATURADO e era, por isso, uma segunda versão do relatório
 * financeiro — mesmos números, outro título. Dinheiro saiu daqui inteiro: tem uma
 * aba só dele, que abre o real em quatro eixos.
 *
 * O que ficou é o que se pergunta olhando a bancada: quem está esperando qual
 * etapa, quantos fecharam, quantas doadoras passaram, quantas receptoras
 * receberam embrião, e quanto sobrevive de oócito até prenhez.
 *
 * DUAS LEITURAS QUE NÃO SE MISTURAM, e a tela diz qual é qual:
 *
 *   FILA      cada serviço em exatamente um lugar. Soma o total de serviços, e
 *             responde "o que está parado onde".
 *   FUNIL     o mesmo material atravessando etapas. As linhas se somam entre si,
 *             e a pergunta é quanto sobrevive de uma para a outra.
 */
const PainelServicos = ({
  linhas, dimensao, granularidade, contexto = {},
  // Doadora e receptora vivem em conjuntos próprios, porque é lá que o animal
  // existe: a view de serviços conta oócito e embrião, e não sabe de quantos
  // animais eles saíram. Receptora vem da TE; `prenhez` traz as conferidas, que
  // é outro número.
  producao = [], receptoras = [], prenhez = [],
}) => {
  const t = useMemo(
    () => resumoOperacional(linhas, { producao, receptoras, prenhez }),
    [linhas, producao, receptoras, prenhez]);

  const fila = useMemo(() => filaPorEtapa(linhas), [linhas]);
  const funil = useMemo(() => funilDeProducao(linhas), [linhas]);
  const porMes = useMemo(() => servicosPorMes(linhas), [linhas]);

  /**
   * O quadro por cliente respeita a dimensão escolhida na barra de filtros.
   *
   * `operacaoPorCliente` agrupa por cliente; quando a dimensão é outra — modelo
   * de cobrança — o agrupamento muda de chave mas a leitura é a mesma, então vale
   * reusar em vez de escrever um segundo agrupador.
   */
  const porDimensao = useMemo(() => {
    if (dimensao === 'client_name' || !dimensao) return operacaoPorCliente(linhas);
    return operacaoPorCliente((linhas || []).map(l => ({
      ...l, client_id: l[dimensao] || '—', client_name: l[dimensao] || '—',
    })));
  }, [linhas, dimensao]);

  // Uma especificação só, para a tela e para o papel. Escrever a coluna duas
  // vezes é como o PDF começa a discordar da tela.
  const COLUNAS = [
    { chave: 'cod', rotulo: 'Serviço', valor: (l) => l.service_code || '—' },
    { chave: 'cli', rotulo: 'Cliente', valor: (l) => l.client_name },
    { chave: 'data', rotulo: 'Data',
      valor: (l) => new Date(l.service_date + 'T12:00:00').toLocaleDateString('pt-BR') },
    // A COLUNA QUE FALTAVA: em que etapa o serviço está agora. A tabela dizia
    // quanto ele rendeu e não dizia onde ele estava.
    { chave: 'etapa', rotulo: 'Etapa',
      valor: (l) => (concluido(l) ? 'Concluído' : etapaAtual(l)?.rotulo || '—') },
    { chave: 'ooc', rotulo: 'Oócitos', numerica: true, valor: (l) => num(l.oocitos) },
    { chave: 'emb', rotulo: 'Embriões', numerica: true, forte: true, valor: (l) => num(l.embrioes) },
    { chave: 'te', rotulo: 'Transf.', numerica: true, valor: (l) => num(l.transferidos) },
    { chave: 'pre', rotulo: 'Prenhes', numerica: true, valor: (l) => num(l.prenhes) },
  ];

  // Os MESMOS valores que os cartões acima mostram, sem recontar nada.
  const imprimir = () => gerarRelatorioPDF({
    titulo: 'Relatório de serviços',
    laboratorio: contexto.laboratorio,
    filtros: descreverFiltros(contexto),
    indicadores: [
      { rotulo: 'Serviços', valor: num(t.servicos),
        detalhe: `${num(t.clientes)} cliente(s) atendido(s)` },
      { rotulo: 'Concluídos', valor: num(t.concluidos),
        detalhe: t.conclusao === null ? 'sem base' : `${pct(t.conclusao)} do período` },
      { rotulo: 'Doadoras', valor: num(t.doadoras), detalhe: 'aspiradas no período' },
      { rotulo: 'Receptoras', valor: num(t.receptoras),
        detalhe: `receberam embrião · ${num(t.receptorasConferidas)} com DG lançado` },
    ],
    grupos: {
      titulo: `Consolidado por ${contexto.agrupamento || 'cliente'}`,
      colunas: [
        { chave: 'k', rotulo: contexto.agrupamento || 'Cliente', valor: (g) => g.rotulo },
        { chave: 's', rotulo: 'Serviços', numerica: true, valor: (g) => num(g.servicos) },
        { chave: 'c', rotulo: 'Concl.', numerica: true, valor: (g) => num(g.concluidos) },
        { chave: 'o', rotulo: 'Oócitos', numerica: true, valor: (g) => num(g.oocitos) },
        { chave: 'e', rotulo: 'Embriões', numerica: true, forte: true, valor: (g) => num(g.embrioes) },
        { chave: 't', rotulo: 'Transf.', numerica: true, valor: (g) => num(g.transferidos) },
        { chave: 'p', rotulo: 'Prenhes', numerica: true, valor: (g) => num(g.prenhes) },
      ],
      linhas: porDimensao,
    },
    detalhe: { titulo: 'Serviços', colunas: COLUNAS, linhas },
  });

  if (!linhas || linhas.length === 0) {
    return <Vazio mensagem="Nenhum serviço no período selecionado." />;
  }

  return (
    <div className="space-y-5">
      {/* ── OS NÚMEROS DO ALTO ─────────────────────────────────────────────
          Serviços lidera porque é a unidade de trabalho; ao lado vêm os três
          números que dizem o tamanho da operação em gente e em animal. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Destaque
          rotulo="Serviços no período" valor={num(t.servicos)}
          detalhe={`${num(t.clientes)} cliente(s) atendido(s) · ${num(t.concluidos)} concluído(s)`}
        />
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Indicador
            rotulo="Concluídos" valor={t.conclusao === null ? '—' : pct(t.conclusao)}
            detalhe={`${num(t.concluidos)} de ${num(t.servicos)}`}
          />
          <Indicador rotulo="Doadoras" valor={num(t.doadoras)} detalhe="aspiradas no período" />
          {/* AS DUAS LEITURAS, uma sobre a outra. A receptora entra na TE; o DG
              é conferência posterior que pode nunca acontecer. Em produção são
              409 contra 4, e é esse buraco que ninguém estava vendo. */}
          <Indicador
            rotulo="Receptoras" valor={num(t.receptoras)}
            detalhe={t.receptoras > 0
              ? `${num(t.receptorasConferidas)} com DG lançado`
              : 'receberam embrião'}
          />
          <Indicador rotulo="Embriões" valor={num(t.embrioes)} detalhe={`${num(t.transferidos)} transferidos`} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── A FILA ───────────────────────────────────────────────────────
            Cada serviço em um lugar só, então a soma fecha com o total. É a
            pergunta "quem está esperando o quê", que a tela não respondia. */}
        <Cartao
          titulo="Onde estão os serviços"
          subtitulo="A etapa contratada que cada um ainda espera. Cada serviço conta uma vez."
        >
          <BarrasHorizontais
            larguraRotulo="w-24"
            dados={[
              ...fila.fila.map(f => ({ rotulo: `Esperando ${f.rotulo}`, valor: f.servicos })),
              { rotulo: 'Concluídos', valor: fila.finalizados },
              // Compra de embrião, diária, venda: não é fila de bancada, e
              // contá-los como prontos inflaria a conclusão.
              ...(fila.semEtapa > 0
                ? [{ rotulo: 'Sem etapa', valor: fila.semEtapa }] : []),
            ]}
            detalhe={(d) => `${d.rotulo}: ${num(d.valor)} de ${num(fila.total)} serviço(s)`}
          />
        </Cartao>

        {/* ── O FUNIL ──────────────────────────────────────────────────────
            Aqui as linhas se somam entre si: é o mesmo material atravessando
            etapas. A conversão de cada degrau é sobre o anterior. */}
        <Cartao
          titulo="Do oócito à prenhez"
          subtitulo="Quanto sobrevive de uma etapa para a outra. A taxa é sobre o degrau de cima."
        >
          <BarrasHorizontais
            larguraRotulo="w-24"
            dados={funil.map(d => ({ rotulo: d.rotulo, valor: d.quantidade, degrau: d }))}
            detalhe={(d) => (d.degrau.conversao === null
              ? `${num(d.degrau.quantidade)} — base do funil`
              : `${num(d.degrau.quantidade)} · ${pct(d.degrau.conversao)} dos ${d.degrau.sobre.toLowerCase()}`)}
          />
        </Cartao>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Cartao
          titulo="Serviços abertos por mês"
          subtitulo="Pela data do serviço — quanto trabalho entrou em cada mês."
        >
          <ColunasMensais
            dados={porMes.map(m => ({
              chave: m.chave, rotulo: rotuloDoMes(m.chave), valor: m.servicos,
            }))}
            unidade="serviço(s)"
          />
        </Cartao>

        <Cartao
          titulo={`Por ${contexto.agrupamento?.toLowerCase() || 'cliente'}`}
          subtitulo="Ordenado por embriões produzidos — quem tem mais serviços raramente é quem tem mais embrião."
        >
          <BarrasHorizontais
            dados={porDimensao.map(g => ({ rotulo: g.rotulo, valor: g.embrioes, grupo: g }))}
            detalhe={(d) => `${num(d.grupo.servicos)} serviço(s) · ${num(d.grupo.embrioes)} embriões`
              + ` · ${num(d.grupo.transferidos)} transferidos · ${num(d.grupo.prenhes)} prenhes`}
          />
        </Cartao>
      </div>

      <Tabela
        titulo="Serviços"
        subtitulo={`${num(linhas.length)} no período`}
        colunas={COLUNAS}
        linhas={linhas.map(l => ({ ...l, chave: l.service_id }))}
        acao={<BotaoPDF onClick={imprimir} />}
      />
    </div>
  );
};

export default PainelServicos;
